import { useState, type FormEvent } from "react";
import { Building2, Mail, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { track } from "@/lib/analytics";
import {
  isMessageIdentifierGuardError,
  messageErrorMessage,
  MESSAGES_SAFETY_LINE,
  useCreateRoomsMessage,
  useRoomsTarget,
} from "@/lib/messages";

export function RoomsForm({
  citySlug,
  facilityId,
}: {
  citySlug: string;
  facilityId: string;
}) {
  const navigate = useNavigate();
  const target = useRoomsTarget(citySlug, facilityId);
  const send = useCreateRoomsMessage(citySlug);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const identifierError = isMessageIdentifierGuardError(send.error);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!subject.trim() || !body.trim() || !target.data?.available) return;
    send.mutate(
      { facilityId, subject: subject.trim(), body: body.trim() },
      {
        onSuccess: (response) => {
          track("rooms_send", {
            conversation_id: response.conversation.id,
            message_id: response.message.id,
            facility_id: facilityId,
          });
          navigate(`/board/messages/${encodeURIComponent(response.conversation.id)}`);
        },
      },
    );
  };

  if (target.isLoading) {
    return <div className="pb-messages-state" role="status">Checking rooms contact…</div>;
  }
  if (target.isError || !target.data) {
    return (
      <div className="pb-messages-state pb-messages-state--error" role="alert">
        <span>The rooms contact could not be checked.</span>
        <button type="button" onClick={() => void target.refetch()}>Retry</button>
      </div>
    );
  }
  if (!target.data.available) {
    return (
      <div className="pb-messages-state pb-messages-rooms-unavailable" role="status">
        <Building2 size={22} aria-hidden="true" />
        <strong>{target.data.facility.name} cannot receive rooms messages.</strong>
        <span>The facility does not have a contact email on PulseBoard.</span>
      </div>
    );
  }

  return (
    <form className="pb-messages-rooms-form" onSubmit={submit}>
      <header>
        <span className="pb-messages-kind" data-kind="rooms"><Building2 size={18} aria-hidden="true" /></span>
        <div>
          <h2>{target.data.facility.name}</h2>
          <p>{target.data.remainingToday} rooms {target.data.remainingToday === 1 ? "message" : "messages"} remaining today</p>
        </div>
      </header>

      <aside className="pb-messages-reply-notice">
        <Mail size={17} aria-hidden="true" />
        <span><strong>Replies arrive in your email inbox.</strong> This message is emailed to the facility; replies do not appear in PulseBoard.</span>
      </aside>

      <label>
        Subject
        <input
          required
          value={subject}
          onChange={(event) => {
            setSubject(event.target.value);
            if (send.isError) send.reset();
          }}
        />
      </label>
      <label>
        Message
        <textarea
          required
          maxLength={4000}
          value={body}
          aria-describedby={identifierError ? "pb-rooms-identifier-error" : "pb-rooms-safety"}
          onChange={(event) => {
            setBody(event.target.value);
            if (send.isError) send.reset();
          }}
        />
        <small>{body.length.toLocaleString("en-AU")} / 4,000</small>
      </label>

      {identifierError ? (
        <p className="pb-messages-identifier-error" id="pb-rooms-identifier-error" role="alert">
          Remove patient-identifying details before sending this message.
        </p>
      ) : send.isError ? (
        <p className="pb-messages-form-error" role="alert">{messageErrorMessage(send.error)}</p>
      ) : null}

      <p className="pb-messages-safety" id="pb-rooms-safety">{MESSAGES_SAFETY_LINE}</p>
      <footer>
        <button
          type="submit"
          disabled={send.isPending || target.data.remainingToday < 1 || !subject.trim() || !body.trim()}
        >
          <Send size={14} aria-hidden="true" />
          {send.isPending ? "Sending…" : "Email the rooms"}
        </button>
      </footer>
    </form>
  );
}
